import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Calendar, FileText, Receipt, Tag, FileDown, Camera, StickyNote } from 'lucide-react';

/**
 * Floating Action Button (FAB) with expandable menu
 * - Click to expand menu items in a column
 * - Background dims when open
 * - Click background or X to collapse
 * - Styled to match existing page cards (blue outlined)
 */
const FloatingActionMenu = ({ appointmentId, customerId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    {
      id: 'appointment',
      label: 'Appointment',
      icon: Calendar,
      action: () => navigate('/jobs', { state: { openIntake: true, preSelectedCustomer: { id: customerId } } })
    },
    {
      id: 'estimate',
      label: 'Estimate',
      icon: FileText,
      action: () => navigate('/create-estimate', { state: { appointmentId, customerId } })
    },
    {
      id: 'invoice',
      label: 'Invoice',
      icon: Receipt,
      action: () => navigate('/create-invoice-simple', { state: { appointmentId, customerId } })
    },
    {
      id: 'label',
      label: 'Label',
      icon: Tag,
      action: () => navigate(`/job/${appointmentId}/label`)
    },
    {
      id: 'pdf',
      label: 'PDF',
      icon: FileDown,
      action: () => window.open(`/api/appointments/${appointmentId}/pdf`, '_blank')
    },
    {
      id: 'photo',
      label: 'Photo',
      icon: Camera,
      action: () => navigate(`/job/${appointmentId}/photos`)
    },
    {
      id: 'note',
      label: 'Note',
      icon: StickyNote,
      action: () => navigate(`/job/${appointmentId}/notes`)
    }
  ];

  const handleItemClick = (item) => {
    setIsOpen(false);
    item.action();
  };

  return (
    <>
      {/* Dimmed Background Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB Container - Left side */}
      <div className="fixed bottom-6 left-6 z-50">
        {/* Menu Items - Expand upward in a column */}
        <div className={`flex flex-col-reverse gap-2 mb-3 transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}>
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-gray-200 shadow-md hover:shadow-lg hover:border-blue-300 transition-all duration-200"
                style={{
                  transitionDelay: isOpen ? `${index * 40}ms` : '0ms'
                }}
              >
                <span className="text-gray-700 font-medium text-sm">{item.label}</span>
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center ml-auto">
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Main FAB Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
            isOpen 
              ? 'bg-gray-600' 
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          <Plus className={`w-6 h-6 text-white transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`} />
        </button>
      </div>
    </>
  );
};

export default FloatingActionMenu;
